import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUArtStudio = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-art-studio" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as an Artist's Studio or Maker Space
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[hsl(var(--brand-primary))]">
            A Creative Space That's Truly Your Own
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Painters, sculptors, woodworkers, ceramicists—we've built ADUs for all kinds of artists. From wide sinks and ventilation to gallery-style lighting and storage, we'll help you create a space that supports your creative process.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Art Studio Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/48e5ae47-5608-472e-a292-2c7ed01c7996.png" 
                  alt="Open layout with bright natural light"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/efb6af6c-d1f3-4135-9097-e5910cb9c84c.png" 
                  alt="Industrial-style space with raw materials"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/55e33f9c-6e09-4ed6-b66c-2764cff17804.png" 
                  alt="Spacious creative workspace with high ceilings"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Artist-Focused Design Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Natural Light from Skylights or Clerestory Windows</h3>
                <p className="text-muted-foreground">
                  Optimal lighting conditions for color accuracy and visual clarity, essential for painting, drawing, and detailed work.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Ventilation for Materials or Kilns</h3>
                <p className="text-muted-foreground">
                  Proper air circulation and exhaust systems to safely handle paints, solvents, clay dust, and high-temperature equipment.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Durable Concrete or Epoxy Flooring</h3>
                <p className="text-muted-foreground">
                  Easy-to-clean, chemical-resistant surfaces that can handle spills, stains, and heavy equipment without damage.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Rolling Tables, Racks, and Wall Rails</h3>
                <p className="text-muted-foreground">
                  Flexible storage and display systems for artwork in progress, finished pieces, and specialized equipment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Build Your Dream Art Studio?</h2>
          <p className="text-lg mb-12 opacity-90">
            Create, display, and work without constraint. Your backyard ADU is your blank canvas.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUArtStudio;